import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './styles/globals.css';
import './theme-components/01-theme-variables.css';
import './theme-components/02-card.css';
import './theme-components/03-section-heading.css';
import './theme-components/04-sub-heading.css';
import './theme-components/05-data-display.css';
import './theme-components/06-buttons.css';
import './theme-components/08-corner-accents.css';
import './theme-components/10-pricing.css';
import './theme-components/13-info-banner.css';
import './theme-components/13-member-badge.css';
import './theme-components/19-claim-booking.css';
import './theme-components/20-feature-selector.css';
import './theme-components/22-rate-card.css';
import './theme-components/23-reservation-card.css';
import './theme-components/24-navigation-footer.css';
import './theme-components/25-counter-input.css';
import './theme-components/26-user-dropdown.css';
import './theme-components/27-option-card.css';
import './theme-components/28-alert.css';
import './theme-components/28-detail-row.css';
import './theme-components/29-guest-info-block.css';
import './theme-components/29-vertical-divider.css';
import './theme-components/30-guest-header.css';
import './theme-components/30-urgency-notice.css';
import './theme-components/31-chat-primitives.css';
import './theme-components/31-financial-impact.css';
import './theme-components/32-guest-summary.css';
import './theme-components/32-suggestion-card.css';
import './theme-components/33-destructive-confirm.css';
import './theme-components/34-user-bubble.css';
import './theme-components/36-early-checkin.css';
import './theme-components/36-stat-cell.css';
import './theme-components/37-early-checkin-flow.css';
import './theme-components/60-workflow-modal-content.css';
import './theme-components/66-personal-info-form.css';
import './theme-components/67-contact-info-form.css';
import './theme-components/68-address-form.css';
import './theme-components/69-preferences-grid.css';
import './theme-components/70-preference-section.css';
import './theme-components/71-privacy-settings.css';
import './theme-components/72-partner-memberships.css';
import './theme-components/80-omnibar-update-notice.css';
import './theme-components/81-range-calendar.css';
import './theme-components/82-rewind.css';
import './theme-components/85-card-hero.css';
import './theme-components/86-date-summary.css';
import './theme-components/87-purpose-chips.css';
import './theme-components/88-stream-card.css';
import './theme-components/89-guest-counter.css';
import './theme-components/104-wellness-guide.css';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { installLanguageBridge, notifyParentLanguageReady } from './utils/languageBridge';
import { setSentryTenant, setSentryUser } from './utils/sentry';
import { ErrorProvider } from './contexts/ErrorContext';
import { hotelService, TenantInfo } from './services/hotel.service';
import { ErrorBoundary, ErrorNotificationContainer, LoadingSpinner, SessionExpiryModal, ErrorModal } from './components/common';
import { initializePageMetadata, updatePageMetadata, getCurrentPageType } from './utils/pageMetadata';

// Lazy load all pages for better performance
const MagicLinkVerifyPage = lazy(() => import('./pages/MagicLinkVerifyPage').then(module => ({ default: module.MagicLinkVerifyPage })));
const OTPVerifyPage = lazy(() => import('./pages/OTPVerifyPage').then(module => ({ default: module.OTPVerifyPage })));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage').then(module => ({ default: module.GoogleCallbackPage })));
const OAuthErrorPage = lazy(() => import('./pages/OAuthErrorPage').then(module => ({ default: module.OAuthErrorPage })));
const ReservationNotFoundPage = lazy(() => import('./pages/ReservationNotFoundPage').then(module => ({ default: module.ReservationNotFoundPage })));
const ProfileCreationPage = lazy(() => import('./pages/ProfileCreationPage').then(module => ({ default: module.ProfileCreationPage })));
const PasskeySetupPage = lazy(() => import('./pages/PasskeySetupPage').then(module => ({ default: module.PasskeySetupPage })));
const TenantNotFoundPage = lazy(() => import('./pages/TenantNotFoundPage').then(module => ({ default: module.TenantNotFoundPage })));
const TenantDashboard = lazy(() => import('./pages/TenantDashboard').then(module => ({ default: module.TenantDashboard })));
const EarlyCheckInPage = lazy(() => import('./pages/EarlyCheckInPage').then(module => ({ default: module.EarlyCheckInPage })));

// Lazy load components that might be large
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute').then(module => ({ default: module.default })));
const GuestOnlyAccess = lazy(() => import('./components/GuestOnlyAccess').then(module => ({ default: module.default })));
const GuestOnlyEmailSent = lazy(() => import('./components/GuestOnlyEmailSent').then(module => ({ default: module.default })));

import apiClient from './services/api';
import { setSessionExpiryHandler, setApiErrorModalHandler, getWorkflowModalCloseHandler } from './services/api';
import { clearSessionCookie, getSessionToken } from './utils/sessionUtils';

// Component for handling page metadata inside Router
const PageMetadataHandler: React.FC<{ tenant: TenantInfo | null }> = ({ tenant }) => {
  const location = useLocation();

  // Update page metadata when route changes
  useEffect(() => {
    if (tenant) {
      const pageType = getCurrentPageType(location.pathname);
      updatePageMetadata(tenant, pageType);
    }
  }, [tenant, location.pathname]);

  return null; // This component doesn't render anything
};

// Loading fallback for lazy-loaded pages
const PageLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const AppContent: React.FC = () => {
  const [guestJourney] = useState<'prospect' | 'pre-stay' | 'in-stay' | 'post-stay'>('prospect'); // we can remove this
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [isSessionValid, setIsSessionValid] = useState<boolean | null>(null); // null = loading, true = valid, false = invalid
  const [isValidatingSession, setIsValidatingSession] = useState(true);
  const [showSessionExpiryModal, setShowSessionExpiryModal] = useState(false);
  const [showApiErrorModal, setShowApiErrorModal] = useState(false);
  const [apiError, setApiError] = useState<any>(null);
  const { theme, isLoading, setThemeFromTenantInfo } = useTheme();
  const { userData, setUserData, clearUserData, fetchJourneyStage } = useUser();
  const hasInitialized = React.useRef(false);
  
  // Function to re-validate session (callable from other components)
  const revalidateSession = React.useCallback(async () => {
    const sessionToken = getSessionToken();

    if (sessionToken) {
      setIsValidatingSession(true);

      try {
        const sessionValidation = await apiClient.validateSession(sessionToken);

        if (sessionValidation.success) {
          setIsSessionValid(true);
        } else {
          setIsSessionValid(false);
          clearSessionCookie();
        }
      } catch (error) {
        setIsSessionValid(false);
        clearSessionCookie();
      }

      setIsValidatingSession(false);
    } else {
      setIsSessionValid(false);
    }
  }, []);
  
  // Function to directly set session as valid (for magic link verification)
  const setSessionValid = React.useCallback(() => {
    setIsSessionValid(true);
  }, []);

  // Handle session expiry
  const handleSessionExpiry = React.useCallback(() => {
    console.log('🔐 Session expired - showing expiry modal');
    setIsSessionValid(false);
    clearUserData();
    setShowSessionExpiryModal(true);
  }, [clearUserData]);

  // Handle sign in redirect from session expiry modal
  const handleSignInRedirect = React.useCallback(() => {
    setShowSessionExpiryModal(false);
    // Force redirect to guest access page
    window.location.href = '/';
  }, []);

  // Handle API errors (500, 404, 403, network errors, etc.)
  const handleApiError = React.useCallback((error: any) => {
    console.error('🚨 Critical API Error - showing error modal:', error);

    // Close any open workflow modal first
    const closeWorkflow = getWorkflowModalCloseHandler();
    if (closeWorkflow) {
      console.log('🚪 Closing workflow modal before showing error modal');
      closeWorkflow();
    }

    setApiError(error);
    setShowApiErrorModal(true);
  }, []);

  // Handle closing API error modal
  const handleCloseApiErrorModal = React.useCallback(() => {
    setShowApiErrorModal(false);
    setApiError(null);
  }, []);
  
  // Expose session management functions to window for access from other components
  React.useEffect(() => {
    (window as any).revalidateSession = revalidateSession;
    (window as any).setSessionValid = setSessionValid;

    // Set up session expiry handler for API client
    setSessionExpiryHandler(handleSessionExpiry);

    // Set up API error modal handler for critical errors
    setApiErrorModalHandler(handleApiError);

    return () => {
      delete (window as any).revalidateSession;
      delete (window as any).setSessionValid;
      setSessionExpiryHandler(() => {});
      setApiErrorModalHandler(() => {});
    };
  }, [revalidateSession, setSessionValid, handleSessionExpiry, handleApiError]);
  
  // Stable references to prevent re-renders
  const setThemeFromTenantInfoRef = React.useRef(setThemeFromTenantInfo);
  const setUserDataRef = React.useRef(setUserData);
  const clearUserDataRef = React.useRef(clearUserData);
  
  React.useEffect(() => {
    setThemeFromTenantInfoRef.current = setThemeFromTenantInfo;
    setUserDataRef.current = setUserData;
    clearUserDataRef.current = clearUserData;
  });

  // Session validation and app initialization
  useEffect(() => {
    // Prevent duplicate initialization in StrictMode
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const initializeApp = async () => {
      // Step 1: Validate session if cookie exists
      const sessionToken = getSessionToken();

      if (sessionToken) {
        setIsValidatingSession(true);

        try {
          const sessionValidation = await apiClient.validateSession(sessionToken);

          if (sessionValidation.success) {
            setIsSessionValid(true);
            // Set user data from session validation
          } else {
            setIsSessionValid(false);
            clearSessionCookie();
          }
        } catch (error) {
          setIsSessionValid(false);
          clearSessionCookie();
        }
      } else {
        setIsSessionValid(false);
      }
      
      setIsValidatingSession(false);
      
      // Step 2: Fetch tenant info
      try {
        setIsLoadingTenant(true);
        setTenantError(null);
        const tenantData = await hotelService.getTenantInfo();
        setTenant(tenantData);
        setThemeFromTenantInfoRef.current(tenantData);

        // Update browser branding (favicon, page title, and meta description)
        if (tenantData) {
          // Initialize page metadata with auth page (default for initial load)
          initializePageMetadata(tenantData, '/');
        }
      } catch (error) {
        setTenantError(`Failed to load hotel information: ${error instanceof Error ? error.message : 'Unknown error'}`);

        // Reset to default title on error
        document.title = 'Hotel Assistant';
      } finally {
        setIsLoadingTenant(false);
      }
    };

    initializeApp();
  }, []); // Empty dependency array - only run once on mount

  // Show loading state while validating session, theme, or tenant data is loading
  if (isValidatingSession || isLoading || isLoadingTenant) {
    return (
      <LoadingSpinner 
        fullScreen
        text={isLoading ? 'Loading theme...' : 'Loading hotel information...'}
      />
    );
  }

  // Show tenant not found page if there's an error or no tenant data
  if (tenantError || !tenant) {
    return (
      <TenantNotFoundPage 
        error={tenantError || 'Hotel information could not be loaded'} 
      />
    );
  }

  // Add guest journey to tenant data
  const tenantWithJourney = {
    ...tenant,
    guestJourney
  };

  return (
    <Router>
      {/* Handle page metadata updates based on route changes */}
      <PageMetadataHandler tenant={tenant} />
      {/* Error notification system */}
      <ErrorNotificationContainer />
      {/* <MagicLinkHandler /> */}
        <Routes>
          {/* Guest Access - Email Entry */}
          <Route path="/" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <GuestOnlyAccess tenant={tenantWithJourney} isSessionValid={isSessionValid} />
            </Suspense>
          } />
          
          {/* Email Sent Confirmation */}
          <Route path="/email-sent" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <GuestOnlyEmailSent tenant={tenantWithJourney} isSessionValid={isSessionValid} />
            </Suspense>
          } />
          
          {/* Magic Link Verification */}
          <Route path="/verify-magic-link" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <MagicLinkVerifyPage tenant={tenantWithJourney} onSessionUpdate={revalidateSession} />
            </Suspense>
          } />

          {/* OTP Code Verification */}
          <Route path="/verify-code" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <OTPVerifyPage tenant={tenantWithJourney} onSessionUpdate={revalidateSession} />
            </Suspense>
          } />

          {/* Google OAuth success — backend redirects here with ?code=...&state=...
              after Google's consent step. SPA POSTs to /auth0/google/auth/callback
              and runs the same post-verify flow as magic-link. */}
          <Route path="/oauth-success" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <GoogleCallbackPage tenant={tenantWithJourney} onSessionUpdate={revalidateSession} />
            </Suspense>
          } />

          {/* Google OAuth error — backend redirects here with an error code
              (e.g. ?error=access_denied) when the user cancels or the flow fails. */}
          <Route path="/oauth-error" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <OAuthErrorPage tenant={tenantWithJourney} />
            </Suspense>
          } />
          
          {/* Profile Creation */}
          <Route path="/profile-creation" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <ProfileCreationPage tenant={tenantWithJourney} />
            </Suspense>
          } />
          
          {/* Passkey Setup - Protected Route */}
          <Route path="/passkey-setup" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <ProtectedRoute isSessionValid={isSessionValid}>
                <Suspense fallback={<PageLoadingFallback />}>
                  <PasskeySetupPage tenant={tenantWithJourney} />
                </Suspense>
              </ProtectedRoute>
            </Suspense>
          } />
          
          {/* Dashboard - Protected Route */}
          <Route path="/myhotelspace" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <ProtectedRoute isSessionValid={isSessionValid}>
                <Suspense fallback={<PageLoadingFallback />}>
                  <TenantDashboard tenant={tenantWithJourney} onLogout={() => window.location.href = '/'} />
                </Suspense>
              </ProtectedRoute>
            </Suspense>
          } />

          {/* Early Check-in - Protected Route */}
          <Route path="/early-checkin" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <ProtectedRoute isSessionValid={isSessionValid}>
                <Suspense fallback={<PageLoadingFallback />}>
                  <EarlyCheckInPage />
                </Suspense>
              </ProtectedRoute>
            </Suspense>
          } />
          
          {/* Other routes */}
          <Route path="/reservation-not-found" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <ReservationNotFoundPage tenant={tenantWithJourney} />
            </Suspense>
          } />
          <Route path="/authenticate-magic-link" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <MagicLinkVerifyPage tenant={tenantWithJourney} onSessionUpdate={revalidateSession} />
            </Suspense>
          } />
          
          {/* Fallback to home */}
          <Route path="*" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <GuestOnlyAccess tenant={tenantWithJourney} isSessionValid={isSessionValid} />
            </Suspense>
          } />
        </Routes>
        
        {/* Session Expiry Modal */}
        <SessionExpiryModal
          isOpen={showSessionExpiryModal}
          onSignIn={handleSignInRedirect}
          autoRedirectSeconds={10}
        />

        {/* Global API Error Modal */}
        <ErrorModal
          isOpen={showApiErrorModal}
          onClose={handleCloseApiErrorModal}
          errorTitle={
            apiError?.code === 'NETWORK_ERROR'
              ? 'Connection Error'
              : apiError?.error?.header || apiError?.header || "We're Sorry for the Inconvenience"
          }
          errorMessage={
            apiError?.error?.detail ||
            apiError?.detail ||
            apiError?.message ||
            "We're unable to process your request at this time due to a temporary technical issue."
          }
          contactPhone={tenant?.contactInfo?.phone || tenant?.phone || "(407) 917-9304"}
          contactEmail={tenant?.contactInfo?.email || tenant?.email || "frontdesk@hotel.com"}
          propertyInfo={{
            name: tenant?.name || theme?.brand?.name || 'Hotel',
            logo: theme?.brand?.logo
          }}
          onExit={handleCloseApiErrorModal}
          fullSize={true}
          showHeader={false}
        />
    </Router>
  );
};

const SentryTagger: React.FC = () => {
  const { tenantData } = useTheme();
  const { userData } = useUser();
  React.useEffect(() => {
    setSentryTenant(tenantData);
  }, [tenantData]);
  React.useEffect(() => {
    if (!userData?.email && !userData?.id) {
      setSentryUser(null);
      return;
    }
    setSentryUser({ id: userData.id, email: userData.email });
  }, [userData?.id, userData?.email]);
  return null;
};

// Bridges language changes from the embedding marketing site into the guest
// SPA. Installs the `cordiant:set-language` listener once and posts the
// `cordiant:ready` handshake so marketing can (re)send the current language.
// Reads hotelId / guestEmail through a ref so the single listener always
// persists against the latest auth state. Renders nothing.
const LanguageBridge: React.FC = () => {
  const { tenantData } = useTheme();
  const { userData } = useUser();

  const ctxRef = useRef<{ hotelId?: string; guestEmail?: string }>({});
  ctxRef.current = { hotelId: tenantData?.id, guestEmail: userData?.email };

  useEffect(() => {
    const teardown = installLanguageBridge(() => ctxRef.current);
    notifyParentLanguageReady();
    return teardown;
  }, []);

  return null;
};

const App: React.FC = () => {
  // Theme is loaded by ThemeProvider - no hardcoded fallback needed
  // ThemeContext will apply tenant theme from API or DEFAULT_THEME as fallback

  return (
    <ErrorBoundary>
      <ErrorProvider>
        <ThemeProvider>
          <UserProvider>
            <SentryTagger />
            <LanguageBridge />
            <AppContent />
          </UserProvider>
        </ThemeProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
};

export default App;